/**
 * Entity API helpers for 3-tier entity resolution
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3100';

/**
 * Check for similar entities (suggest)
 * @param {string} mention - The mention text
 * @param {string} context - Context where mention appeared
 * @param {string} sessionId - Current session ID
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Suggestion result
 */
export async function suggestEntity(mention, context, sessionId, accessToken) {
  const response = await fetch(`${API_URL}/api/entities/suggest`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({ mention, context, sessionId })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to suggest entity');
  }
  
  return response.json();
}

/**
 * Accept an entity alias suggestion
 * @param {string} mention - The mention text
 * @param {string} entityId - Entity ID to link to
 * @param {boolean} permanent - If true, add permanent alias. If false, session only.
 * @param {string} sessionId - Current session ID
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Result
 */
export async function acceptEntitySuggestion(mention, entityId, permanent, sessionId, accessToken) {
  const response = await fetch(`${API_URL}/api/entities/accept`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({ mention, entityId, permanent, sessionId })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to accept suggestion');
  }
  
  return response.json();
}

/**
 * Reject an entity suggestion for this session
 * @param {string} mention - The mention text
 * @param {string} sessionId - Current session ID
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Result
 */
export async function rejectEntitySuggestion(mention, sessionId, accessToken) {
  const response = await fetch(`${API_URL}/api/entities/reject`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({ mention, sessionId })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to reject suggestion');
  }
  
  return response.json();
}

/**
 * Add item to review queue (Tier 3)
 * @param {object} data - { mention, entityId, entityName, context, score, reason }
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Result
 */
export async function queueForReview(data, accessToken) {
  const response = await fetch(`${API_URL}/api/entities/queue`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify(data)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to queue for review');
  }
  
  return response.json();
}

/**
 * Get pending review items
 * @param {string} accessToken - Kinde access token
 * @param {number} limit - Max items to return
 * @returns {Promise<object>} { items, count }
 */
export async function getReviewQueue(accessToken, limit = 20) {
  const response = await fetch(`${API_URL}/api/entities/review-queue?limit=${limit}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to get review queue');
  }
  
  return response.json();
}

/**
 * Get pending review count
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} { count }
 */
export async function getReviewQueueCount(accessToken) {
  const response = await fetch(`${API_URL}/api/entities/review-queue/count`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    return { count: 0 };
  }
  
  return response.json();
}

/**
 * Review a queued item
 * @param {string} reviewId - Review item ID
 * @param {string} decision - 'merge' | 'separate' | 'skip'
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} Result
 */
export async function reviewEntityItem(reviewId, decision, accessToken) {
  const response = await fetch(`${API_URL}/api/entities/review/${reviewId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({ decision })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to review item');
  }
  
  return response.json();
}

/**
 * Get review statistics
 * @param {string} accessToken - Kinde access token
 * @returns {Promise<object>} { stats: { pending, merged, separated, skipped } }
 */
export async function getEntityStats(accessToken) {
  const response = await fetch(`${API_URL}/api/entities/stats`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    return { stats: { pending: 0, merged: 0, separated: 0, skipped: 0 } };
  }
  
  return response.json();
}
